import Decimal from 'decimal.js';
import { isCronosIncomePayoutContract, mergeIncomePayerContracts } from './cronosIncomeContracts';
import { fiMetaForCronosInternalRow } from './fiCronosLedger';
import {
  fiSubreasonForExplorerIncome,
  rationaleForExplorerIncome,
} from './fiIncomeTagging';
import { hintForNativeTxRow, hintForTokentxRow } from './cronosImport';
import { normalizeHeader, parseCsv } from './parseCsv';
import {
  buildTokenRegistryFromTokentxRows,
  displayTokenSymbolFromExplorerRow,
  normalizeContractAddress,
  normalizeTokenDecimalsFromExplorerRow,
  txAppearsSuccessfulFromTxlistRows,
} from './tokenRegistry';
import type { FiCsvDiagnosticRow } from './fiDiagnostics';
import type { CronosPriceHint, LedgerClassification, OwnedWallet } from './types';
import { labelForAddress } from './ownedWallets';
import type {
  AggregatedExplorerEvent,
  CompiledExplorerImportResult,
  ExplorerRecordType,
  NormalizedExplorerLedgerRow,
  TaxTransactionClassification,
  TaxableStatus,
} from './explorerCompiledTypes';
import type { TokenRegistryEntry } from './types';

/** Normalized explorer ledger → FI diagnostics (parity with live Cronos + CSV). */
export function buildFiDiagnosticsFromExplorerLedger(
  ledger: NormalizedExplorerLedgerRow[]
): FiCsvDiagnosticRow[] {
  const out: FiCsvDiagnosticRow[] = [];
  for (const r of ledger) {
    if (!r.hash) continue;

    const nonTax =
      r.taxableStatus === 'non_taxable' ||
      r.normalizedTransactionType === 'INTERNAL_TRANSFER' ||
      r.normalizedTransactionType === 'SELF_TRANSFER' ||
      r.normalizedTransactionType === 'APPROVAL' ||
      r.normalizedTransactionType === 'IGNORE_NON_TAX';

    if (nonTax) {
      const cls =
        r.normalizedTransactionType === 'SELF_TRANSFER'
          ? 'SELF_TRANSFER'
          : r.normalizedTransactionType === 'APPROVAL'
            ? 'APPROVAL'
            : r.normalizedTransactionType === 'IGNORE_NON_TAX'
              ? 'IGNORE_NON_TAX'
              : 'INTERNAL_TRANSFER';
      const meta = fiMetaForCronosInternalRow(cls as LedgerClassification);
      out.push({
        source: 'explorer_compiled',
        csv_row: 0,
        ref: r.hash,
        transaction_kind: r.normalizedTransactionType,
        asset: r.assetSent ?? r.assetReceived ?? 'CRO',
        category: 'NON_TAXABLE_INTERNAL',
        subreason: meta.fiSubreason,
        note: r.explanation,
      });
    }

    if (r.warningFlags?.includes('internal_only_ambiguous_ownership')) {
      out.push({
        source: 'explorer_compiled',
        csv_row: 0,
        ref: r.hash,
        transaction_kind: r.normalizedTransactionType,
        asset: r.assetReceived ?? r.assetSent ?? 'CRO',
        category: 'AMBIGUOUS_REVIEW',
        subreason: 'TXLISTINTERNAL_AMBIGUOUS_OWNERSHIP',
        note: r.explanation,
      });
    }
  }
  return out;
}

function str(v: unknown): string {
  return v == null ? '' : String(v).trim();
}

function num(v: unknown): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

/** Recognize explorer_record_type with flexible naming. */
export function getExplorerRecordType(row: Record<string, unknown>): ExplorerRecordType | null {
  const raw =
    str(
      row.explorer_record_type ??
        row.explorerrecordtype ??
        row['explorer_record_type']
    ).toLowerCase() || '';
  if (raw === 'tokentx' || raw === 'token_tx' || raw === 'erc20') return 'tokentx';
  if (raw === 'txlist' || raw === 'tx_list' || raw === 'transaction') return 'txlist';
  if (
    raw === 'txlistinternal' ||
    raw === 'internal' ||
    raw === 'tx_list_internal'
  ) {
    return 'txlistinternal';
  }
  return null;
}

function txHashFromRow(row: Record<string, unknown>): string {
  return str(row.hash ?? row.transactionHash ?? row.txHash ?? '').toLowerCase();
}

export type ParseCompiledExplorerDatasetOptions = {
  /** Merged with built-in `CRONOS_INCOME_PAYOUT_CONTRACTS` for staking/income classification. */
  incomePayerAddressLines?: string[];
};

/** Parses merged explorer CSV; `interpretAggregate` classifies each tx (incl. router internal CRO) before EUR pricing. */
export function parseCompiledExplorerDataset(
  csvText: string,
  wallets: OwnedWallet[],
  options?: ParseCompiledExplorerDatasetOptions
): CompiledExplorerImportResult {
  const parseErrors: string[] = [];
  const warnings: string[] = [];
  const grid = parseCsv(csvText);
  if (grid.length < 2) {
    return {
      ledger: [],
      tokenRegistry: [],
      aggregates: new Map(),
      unbucketedRows: [],
      warnings: ['CSV has no data rows.'],
      rawRowCount: 0,
      parseErrors: [],
      fiDiagnostics: [],
    };
  }

  const headerCells = grid[0].map((h) => normalizeHeader(h));
  const rows: Record<string, unknown>[] = [];
  for (let r = 1; r < grid.length; r++) {
    const line = grid[r];
    if (!line.some((c) => str(c))) continue;
    const obj: Record<string, unknown> = {};
    for (let c = 0; c < headerCells.length; c++) {
      const key = headerCells[c];
      if (!key) continue;
      obj[key] = line[c] != null ? line[c] : '';
    }
    rows.push(obj);
  }

  const ownedSet = new Set(wallets.map((w) => w.address.toLowerCase()));
  const incomePayers = mergeIncomePayerContracts(options?.incomePayerAddressLines);
  const byHash = new Map<string, Record<string, unknown>[]>();
  const unbucketed: Record<string, unknown>[] = [];

  for (const row of rows) {
    const h = txHashFromRow(row);
    if (!h) {
      unbucketed.push(row);
      warnings.push(
        'Row without hash — listed as unbucketed (check CSV). explorer_record_type=' +
          str(getExplorerRecordType(row))
      );
      continue;
    }
    const list = byHash.get(h) ?? [];
    list.push(row);
    byHash.set(h, list);
  }

  const aggregates = new Map<string, AggregatedExplorerEvent>();
  for (const [hash, allRows] of Array.from(byHash.entries())) {
    aggregates.set(hash, buildAggregateForHash(hash, allRows));
  }

  const tokentxOnly = rows.filter((r) => getExplorerRecordType(r) === 'tokentx');
  const registryMap = buildTokenRegistryFromTokentxRows(tokentxOnly, ownedSet);
  const tokenRegistry: TokenRegistryEntry[] = Array.from(registryMap.values()).sort((a, b) =>
    a.contractAddress.localeCompare(b.contractAddress)
  );

  const ledger: NormalizedExplorerLedgerRow[] = [];

  for (const agg of Array.from(aggregates.values())) {
    ledger.push(interpretAggregate(agg, ownedSet, wallets, registryMap, incomePayers));
  }

  for (const raw of unbucketed) {
    ledger.push(
      interpretUnbucketedRow(raw, ownedSet, wallets)
    );
  }

  ledger.sort((a, b) => a.timestampMs - b.timestampMs);

  const fiDiagnostics = buildFiDiagnosticsFromExplorerLedger(ledger);

  return {
    ledger,
    tokenRegistry,
    aggregates,
    unbucketedRows: unbucketed,
    warnings,
    rawRowCount: rows.length,
    parseErrors,
    fiDiagnostics,
  };
}

function buildAggregateForHash(
  hash: string,
  allRows: Record<string, unknown>[]
): AggregatedExplorerEvent {
  const txlist: Record<string, unknown>[] = [];
  const tokentx: Record<string, unknown>[] = [];
  const txlistinternal: Record<string, unknown>[] = [];
  let minTs = Number.POSITIVE_INFINITY;

  for (const row of allRows) {
    const rt = getExplorerRecordType(row);
    if (rt === 'txlist') txlist.push(row);
    else if (rt === 'tokentx') tokentx.push(row);
    else if (rt === 'txlistinternal') txlistinternal.push(row);
    const tsSec = num(row.timestamp ?? row.timeStamp);
    if (tsSec > 0) minTs = Math.min(minTs, tsSec * 1000);
  }

  if (!Number.isFinite(minTs)) minTs = 0;

  const primaryFrom =
    str(txlist[0]?.from ?? tokentx[0]?.from ?? txlistinternal[0]?.from ?? '');
  const primaryTo =
    str(txlist[0]?.to ?? tokentx[0]?.to ?? txlistinternal[0]?.to ?? '');

  return {
    hash,
    timestampMs: minTs,
    txlist,
    tokentx,
    txlistinternal,
    allRawRows: allRows,
    primaryFrom: primaryFrom.toLowerCase(),
    primaryTo: primaryTo.toLowerCase(),
    interpretationNotes: [],
  };
}

function weiLikeToNative(valueStr: string): Decimal {
  try {
    return new Decimal(valueStr || '0').div(new Decimal(10).pow(18));
  } catch {
    return new Decimal(0);
  }
}

function tokenAmount(raw: string, decimals: number): { norm: Decimal; rawStr: string } {
  const rawStr = String(raw ?? '0');
  try {
    return {
      norm: new Decimal(rawStr).div(new Decimal(10).pow(decimals)),
      rawStr,
    };
  } catch {
    return { norm: new Decimal(0), rawStr };
  }
}

function estimateFeeFromTxlist(txlistRows: Record<string, unknown>[]): {
  feeAmount: Decimal | null;
  uncertainty: boolean;
} {
  if (txlistRows.length === 0) return { feeAmount: null, uncertainty: true };
  const row = txlistRows[0];
  try {
    const gasUsed = BigInt(str(row.gasUsed || row.gas || '0') || '0');
    const gasPrice = BigInt(str(row.gasPrice || '0') || '0');
    const wei = gasUsed * gasPrice;
    const fee = new Decimal(wei.toString()).div(new Decimal(10).pow(18));
    return { feeAmount: fee, uncertainty: false };
  } catch {
    return { feeAmount: null, uncertainty: true };
  }
}

function nativeTransferFromTxlist(
  row: Record<string, unknown>
): { amount: Decimal; raw: string } | null {
  const v = str(row.value);
  if (!v || v === '0') return null;
  const amount = weiLikeToNative(v);
  if (amount.lte(0)) return null;
  return { amount, raw: v };
}

/**
 * Router swaps often deliver WCRO/native CRO via internal calls only — no ERC-20 `tokentx` row for the
 * incoming leg. Sum positive native value to an owned address from `txlistinternal` rows.
 * Runs during `parseCompiledExplorerDataset` / merge (before any EUR fetch) so the ledger already has
 * a SWAP + CRO in-leg when the user clicks “Fetch EUR”.
 */
function sumIncomingNativeCroToOwnedFromInternal(
  agg: AggregatedExplorerEvent,
  ownedSet: Set<string>
): Decimal | null {
  let sum = new Decimal(0);
  for (const row of agg.txlistinternal) {
    const to = str(row.to ?? '').toLowerCase();
    if (!to || !ownedSet.has(to)) continue;
    const v = weiLikeToNative(str(row.value ?? '0'));
    if (v.gt(0)) sum = sum.plus(v);
  }
  return sum.gt(0) ? sum : null;
}

function interpretAggregate(
  agg: AggregatedExplorerEvent,
  ownedSet: Set<string>,
  wallets: OwnedWallet[],
  _registry: Map<string, TokenRegistryEntry>,
  incomePayers: Set<string>
): NormalizedExplorerLedgerRow {
  const hasTx = agg.txlist.length > 0;
  const hasTok = agg.tokentx.length > 0;
  const hasInt = agg.txlistinternal.length > 0;
  const sourceRecordCount = agg.allRawRows.length;
  const parts: string[] = [];
  if (hasTok) parts.push(`${agg.tokentx.length} tokentx`);
  if (hasTx) parts.push(`${agg.txlist.length} txlist`);
  if (hasInt) parts.push(`${agg.txlistinternal.length} internal`);
  const sourceSummary = parts.length ? parts.join(', ') : 'no typed rows';

  const fromA = agg.primaryFrom;
  const toA = agg.primaryTo;
  const fromOwned = ownedSet.has(fromA);
  const toOwned = ownedSet.has(toA);
  const primaryWallet = fromOwned ? fromA : toOwned ? toA : fromA || toA;
  const primaryLabel = primaryWallet
    ? labelForAddress(wallets, primaryWallet)
    : undefined;

  const txOk = txAppearsSuccessfulFromTxlistRows(agg.txlist);
  const { feeAmount, uncertainty: feeUncertainty } = estimateFeeFromTxlist(agg.txlist);
  const feePayer = str(agg.txlist[0]?.from ?? '');

  let interpretationBasis =
    'Merged explorer rows by hash; interpretation follows tokentx > txlist > internal.';
  if (hasTok && hasTx) {
    interpretationBasis =
      'Derived primarily from tokentx rows, enriched with txlist gas/native context.';
  } else if (hasTok && !hasTx) {
    interpretationBasis = 'Derived primarily from tokentx rows.';
  } else if (hasTx && !hasTok) {
    interpretationBasis =
      'Derived from txlist (and txlistinternal if present); native / contract context.';
  } else if (hasInt && !hasTok && !hasTx) {
    interpretationBasis = 'Derived from txlistinternal rows only (limited context).';
  }

  /** Token legs */
  type Leg = {
    contract: string;
    symbol: string;
    from: string;
    to: string;
    amount: Decimal;
    raw: string;
    decimals: number;
  };
  const legs: Leg[] = [];
  for (const row of agg.tokentx) {
    const contract = normalizeContractAddress(
      str(row.contractAddress ?? row.contract_address)
    );
    const decimals = normalizeTokenDecimalsFromExplorerRow(row);
    const { norm, rawStr } = tokenAmount(str(row.value), decimals);
    if (norm.lte(0)) continue;
    legs.push({
      contract,
      symbol: displayTokenSymbolFromExplorerRow(row, contract),
      from: str(row.from).toLowerCase(),
      to: str(row.to).toLowerCase(),
      amount: norm,
      raw: rawStr,
      decimals,
    });
  }

  let normalizedTransactionType: TaxTransactionClassification = 'UNKNOWN_REVIEW';
  let confidence: 'HIGH' | 'MEDIUM' | 'LOW' = 'LOW';
  const warningFlags: string[] = [];
  let assetSent: string | undefined;
  let amountSent: Decimal | null = null;
  let rawValueSent: string | undefined;
  let assetReceived: string | undefined;
  let amountReceived: Decimal | null = null;
  let rawValueReceived: string | undefined;
  let tokenContract: string | undefined;
  let explanation = '';
  let taxableStatus: TaxableStatus = 'unknown';

  const outs = legs.filter((l) => ownedSet.has(l.from) && !ownedSet.has(l.to));
  const ins = legs.filter((l) => !ownedSet.has(l.from) && ownedSet.has(l.to));
  const internalTok = legs.filter(
    (l) => ownedSet.has(l.from) && ownedSet.has(l.to) && l.from !== l.to
  );
  const selfTok = legs.filter(
    (l) => ownedSet.has(l.from) && l.from === l.to && l.amount.gt(0)
  );

  if (legs.length > 0) {
    if (internalTok.length > 0 && outs.length === 0 && ins.length === 0) {
      normalizedTransactionType = 'INTERNAL_TRANSFER';
      confidence = 'HIGH';
      taxableStatus = 'non_taxable';
      const l = internalTok[0];
      assetSent = l.symbol;
      amountSent = l.amount;
      rawValueSent = l.raw;
      assetReceived = l.symbol;
      amountReceived = l.amount;
      rawValueReceived = l.raw;
      tokenContract = l.contract;
      explanation = 'Token movement between two owned addresses (tokentx).';
    } else if (selfTok.length > 0 && legs.length === selfTok.length) {
      normalizedTransactionType = 'SELF_TRANSFER';
      confidence = 'MEDIUM';
      taxableStatus = 'non_taxable';
      const l = selfTok[0];
      assetSent = l.symbol;
      amountSent = l.amount;
      explanation = 'Same-address token movement (self).';
    } else if (outs.length >= 1 && ins.length >= 1) {
      const outC = new Set(outs.map((o) => o.contract));
      const inC = new Set(ins.map((i) => i.contract));
      let different = false;
      for (const o of Array.from(outC)) {
        for (const i of Array.from(inC)) {
          if (o && i && o !== i) {
            different = true;
            break;
          }
        }
      }
      if (different || (outs.length >= 1 && ins.length >= 1 && legs.length >= 2)) {
        normalizedTransactionType = 'SWAP';
        confidence = 'MEDIUM';
        taxableStatus = 'taxable';
        const o = outs[0];
        const i = ins[0];
        assetSent = o.symbol;
        amountSent = o.amount;
        rawValueSent = o.raw;
        assetReceived = i.symbol;
        amountReceived = i.amount;
        rawValueReceived = i.raw;
        tokenContract = o.contract || i.contract;
        explanation =
          'Multiple token legs involving owned wallet — classified as swap (review).';
        warningFlags.push('swap_heuristic');
      } else {
        normalizedTransactionType = 'TRANSFER_OUT';
        confidence = 'HIGH';
        taxableStatus = 'taxable';
        const o = outs[0];
        assetSent = o.symbol;
        amountSent = o.amount;
        rawValueSent = o.raw;
        tokenContract = o.contract;
        explanation = 'Outgoing token transfer from owned address.';
      }
    } else if (ins.length >= 1 && outs.length === 0) {
      const i = ins[0];
      assetReceived = i.symbol;
      amountReceived = i.amount;
      rawValueReceived = i.raw;
      tokenContract = i.contract;
      if (isCronosIncomePayoutContract(i.from, incomePayers)) {
        normalizedTransactionType = 'STAKING_REWARD';
        confidence = 'HIGH';
        taxableStatus = 'taxable';
        explanation =
          'Incoming tokens from a configured staking/income payer contract (Cronos income allowlist).';
        warningFlags.push('income_contract_allowlist');
      } else {
        normalizedTransactionType = 'TRANSFER_IN';
        confidence = 'HIGH';
        taxableStatus = 'taxable';
        explanation = 'Incoming token transfer to owned address.';
      }
    } else if (outs.length >= 1 && ins.length === 0) {
      const internalCroIn = sumIncomingNativeCroToOwnedFromInternal(agg, ownedSet);
      if (internalCroIn != null && internalCroIn.gt(0)) {
        normalizedTransactionType = 'SWAP';
        confidence = 'MEDIUM';
        taxableStatus = 'taxable';
        const o = outs[0]!;
        assetSent = o.symbol;
        amountSent = o.amount;
        rawValueSent = o.raw;
        assetReceived = 'CRO';
        amountReceived = internalCroIn;
        tokenContract = o.contract;
        explanation =
          'Token out with native CRO to wallet via internal tx (router / multicall) — classified as swap.';
        warningFlags.push('swap_internal_native_cro_in');
      } else {
        normalizedTransactionType = 'TRANSFER_OUT';
        confidence = 'HIGH';
        taxableStatus = 'taxable';
        const o = outs[0];
        assetSent = o.symbol;
        amountSent = o.amount;
        rawValueSent = o.raw;
        tokenContract = o.contract;
        explanation = 'Outgoing token transfer.';
      }
    } else {
      normalizedTransactionType = 'UNKNOWN_REVIEW';
      warningFlags.push('ambiguous_token_legs');
      explanation = 'Token legs could not be classified cleanly — manual review.';
    }
  }

  /** Native-only path */
  if (!hasTok && hasTx) {
    const row = agg.txlist[0];
    const nt = nativeTransferFromTxlist(row);
    const from = str(row.from).toLowerCase();
    const to = str(row.to).toLowerCase();
    const fo = ownedSet.has(from);
    const tO = ownedSet.has(to);
    if (nt) {
      if (fo && tO && from !== to) {
        normalizedTransactionType = 'INTERNAL_TRANSFER';
        taxableStatus = 'non_taxable';
        assetSent = 'CRO';
        amountSent = nt.amount;
        assetReceived = 'CRO';
        amountReceived = nt.amount;
        explanation = 'Native CRO movement between owned wallets (txlist).';
        interpretationBasis =
          'Derived from txlist native transfer; no tokentx in aggregate.';
      } else if (fo && !tO) {
        normalizedTransactionType = 'TRANSFER_OUT';
        taxableStatus = 'taxable';
        assetSent = 'CRO';
        amountSent = nt.amount;
        rawValueSent = nt.raw;
        explanation = 'Native CRO outgoing from owned address.';
      } else if (!fo && tO) {
        normalizedTransactionType = 'TRANSFER_IN';
        taxableStatus = 'taxable';
        assetReceived = 'CRO';
        amountReceived = nt.amount;
        rawValueReceived = nt.raw;
        explanation = 'Native CRO incoming to owned address.';
      }
      confidence = 'HIGH';
    } else if (str(row.input).length > 10 && nt == null) {
      normalizedTransactionType = 'APPROVAL';
      taxableStatus = 'non_taxable';
      warningFlags.push('possible_contract_interaction');
      explanation =
        'Contract interaction with little/no native value — possible approval or complex call.';
    }
  }

  if (
    normalizedTransactionType === 'UNKNOWN_REVIEW' &&
    legs.length === 0 &&
    !hasTx &&
    hasInt
  ) {
    const ir = agg.txlistinternal[0];
    const iv = weiLikeToNative(str(ir.value));
    if (iv.gt(0)) {
      warningFlags.push('internal_only_evidence');
      assetSent = 'CRO';
      amountSent = iv;
      assetReceived = 'CRO';
      amountReceived = iv;
      if (fromOwned && toOwned) {
        if (fromA === toA) {
          normalizedTransactionType = 'SELF_TRANSFER';
          taxableStatus = 'non_taxable';
          confidence = 'MEDIUM';
          explanation =
            'Same-address internal value (txlistinternal only) — non-taxable self-movement.';
        } else {
          normalizedTransactionType = 'INTERNAL_TRANSFER';
          taxableStatus = 'non_taxable';
          confidence = 'MEDIUM';
          explanation =
            'Native CRO between owned wallets (txlistinternal only; limited explorer context).';
        }
      } else {
        normalizedTransactionType = 'UNKNOWN_REVIEW';
        taxableStatus = 'unknown';
        warningFlags.push('internal_only_ambiguous_ownership');
        explanation =
          'Internal tx value only — ownership partly external; review whether this is taxable inflow/outflow.';
      }
    }
  }

  if (txOk === false && hasTx) {
    normalizedTransactionType = 'IGNORE_NON_TAX';
    taxableStatus = 'non_taxable';
    confidence = 'HIGH';
    assetSent = undefined;
    amountSent = null;
    rawValueSent = undefined;
    assetReceived = undefined;
    amountReceived = null;
    rawValueReceived = undefined;
    tokenContract = undefined;
    explanation =
      'Transaction failed or reverted on-chain; no economic token or native transfer (explorer may still list attempted legs).';
    warningFlags.length = 0;
    warningFlags.push('tx_reverted_or_failed');
  }

  const missingCostBasisDefaulted = true;
  if (!(txOk === false && hasTx)) {
    warningFlags.push('missing_cost_basis');
  }

  return {
    hash: agg.hash,
    timestampMs: agg.timestampMs,
    sourceSummary,
    sourceRecordCount,
    hasTxlistRecord: hasTx,
    hasTokentxRecord: hasTok,
    hasTxlistinternalRecord: hasInt,
    fromAddress: fromA,
    toAddress: toA,
    fromOwned,
    toOwned,
    primaryWalletLabel: primaryLabel,
    normalizedTransactionType,
    assetSent,
    amountSent,
    rawValueSent,
    assetReceived,
    amountReceived,
    rawValueReceived,
    feeAsset: 'CRO',
    feeAmount,
    feePayer: feePayer || undefined,
    feeUncertainty,
    tokenContractAddress: tokenContract,
    estimatedEurValue: null,
    acquisitionCostBasisEur: new Decimal(0),
    proceedsEur: null,
    gainOrLossEur: null,
    taxableStatus,
    confidence,
    warningFlags: Array.from(new Set(warningFlags)),
    interpretationBasis,
    explanation,
    txSuccess: txOk,
    missingCostBasisDefaulted,
    aggregate: agg,
  };
}

function interpretUnbucketedRow(
  row: Record<string, unknown>,
  ownedSet: Set<string>,
  wallets: OwnedWallet[]
): NormalizedExplorerLedgerRow {
  const from = str(row.from).toLowerCase();
  const to = str(row.to).toLowerCase();
  const fromOwned = ownedSet.has(from);
  const toOwned = ownedSet.has(to);
  const tsSec = num(row.timestamp ?? row.timeStamp);
  return {
    hash: '',
    timestampMs: tsSec > 0 ? tsSec * 1000 : 0,
    sourceSummary: 'unbucketed (no hash)',
    sourceRecordCount: 1,
    hasTxlistRecord: getExplorerRecordType(row) === 'txlist',
    hasTokentxRecord: getExplorerRecordType(row) === 'tokentx',
    hasTxlistinternalRecord: getExplorerRecordType(row) === 'txlistinternal',
    fromAddress: from,
    toAddress: to,
    fromOwned,
    toOwned,
    primaryWalletLabel: fromOwned
      ? labelForAddress(wallets, from)
      : toOwned
        ? labelForAddress(wallets, to)
        : undefined,
    normalizedTransactionType: 'UNKNOWN_REVIEW',
    amountSent: null,
    amountReceived: null,
    feeAsset: 'CRO',
    feeAmount: null,
    feeUncertainty: true,
    estimatedEurValue: null,
    acquisitionCostBasisEur: new Decimal(0),
    proceedsEur: null,
    gainOrLossEur: null,
    taxableStatus: 'unknown',
    confidence: 'LOW',
    warningFlags: ['missing_hash', 'missing_cost_basis'],
    interpretationBasis: 'Row could not be grouped by hash — needs manual mapping.',
    explanation: 'No transaction hash on row; retained for visibility.',
    txSuccess: 'unknown',
    missingCostBasisDefaulted: true,
  };
}

/** Best-effort Cronos price hint for EUR backfill (same shape as live Cronos import). */
function compiledPriceHint(
  r: NormalizedExplorerLedgerRow,
  legAsset: string
): CronosPriceHint | undefined {
  if (r.normalizedTransactionType === 'UNKNOWN_REVIEW') return undefined;
  const agg = r.aggregate;
  if (!agg) return undefined;
  const sym = legAsset.toUpperCase();
  const tokRows = agg.tokentx.filter(
    (t) => str(t.tokenSymbol ?? t.token_symbol).toUpperCase() === sym
  );
  if (tokRows.length > 0) {
    return hintForTokentxRow(tokRows[0]);
  }
  if (sym === 'CRO' && agg.txlist.length > 0) {
    return hintForNativeTxRow(agg.txlist[0]);
  }
  if (agg.tokentx.length > 0) {
    return hintForTokentxRow(agg.tokentx[0]);
  }
  if (agg.txlist.length > 0) {
    return hintForNativeTxRow(agg.txlist[0]);
  }
  return undefined;
}

/**
 * Convert normalized ledger rows to legacy `LedgerEvent[]` for FIFO / EUR pricing where possible.
 * Does not drop rows; internal/non-taxable rows use `internal_transfer` and null EUR.
 */
export function normalizedLedgerToLedgerEvents(
  rows: NormalizedExplorerLedgerRow[]
): import('./types').LedgerEvent[] {
  const out: import('./types').LedgerEvent[] = [];
  for (const r of rows) {
    if (!r.hash) {
      out.push({
        ts: r.timestampMs,
        source: 'cronos',
        kind: 'acquisition',
        asset: 'UNKNOWN',
        amount: new Decimal(0),
        eurValue: null,
        ref: undefined,
        classification: 'UNKNOWN_REVIEW',
        confidence: r.confidence,
        reviewFlags: r.warningFlags,
        explanation: r.explanation,
        fromAddress: r.fromAddress,
        toAddress: r.toAddress,
        primaryWalletAddress: r.fromOwned ? r.fromAddress : r.toAddress,
        primaryWalletLabel: r.primaryWalletLabel,
      });
      continue;
    }

    const nonTax =
      r.taxableStatus === 'non_taxable' ||
      r.normalizedTransactionType === 'INTERNAL_TRANSFER' ||
      r.normalizedTransactionType === 'SELF_TRANSFER' ||
      r.normalizedTransactionType === 'APPROVAL' ||
      r.normalizedTransactionType === 'IGNORE_NON_TAX';

    if (nonTax) {
      const cls =
        r.normalizedTransactionType === 'SELF_TRANSFER'
          ? 'SELF_TRANSFER'
          : r.normalizedTransactionType === 'APPROVAL'
            ? 'APPROVAL'
            : r.normalizedTransactionType === 'IGNORE_NON_TAX'
              ? 'IGNORE_NON_TAX'
              : 'INTERNAL_TRANSFER';
      out.push({
        ts: r.timestampMs,
        source: 'cronos',
        kind: 'internal_transfer',
        asset: r.assetSent ?? r.assetReceived ?? 'CRO',
        amount: r.amountSent ?? r.amountReceived ?? new Decimal(0),
        eurValue: null,
        ref: r.hash,
        classification: cls,
        ...fiMetaForCronosInternalRow(cls),
        confidence: r.confidence,
        reviewFlags: r.warningFlags,
        explanation: r.explanation,
        fromAddress: r.fromAddress,
        toAddress: r.toAddress,
        primaryWalletAddress: r.fromOwned ? r.fromAddress : r.toAddress,
        primaryWalletLabel: r.primaryWalletLabel,
        sourceRecordCount: r.sourceRecordCount,
        sourceSummary: r.sourceSummary,
        hasTransactionRecord: r.hasTxlistRecord,
        hasTokenTransferRecord: r.hasTokentxRecord,
        hasInternalTxRecord: r.hasTxlistinternalRecord,
        interpretationBasis: 'token_transfer_primary',
      });
      continue;
    }

    if (
      (r.normalizedTransactionType === 'STAKING_REWARD' ||
        r.normalizedTransactionType === 'AIRDROP' ||
        r.normalizedTransactionType === 'INCOME') &&
      r.amountReceived
    ) {
      const tx = r.normalizedTransactionType as 'STAKING_REWARD' | 'AIRDROP' | 'INCOME';
      out.push({
        ts: r.timestampMs,
        source: 'cronos',
        kind: 'income',
        asset: r.assetReceived ?? 'UNKNOWN',
        amount: r.amountReceived,
        eurValue: null,
        cronosPriceHint: compiledPriceHint(r, r.assetReceived ?? 'CRO'),
        ref: r.hash,
        classification: 'INCOME',
        fiTaxCategory: 'TAXABLE_INCOME',
        fiSubreason: fiSubreasonForExplorerIncome(tx),
        explanation: rationaleForExplorerIncome(tx),
        confidence: r.confidence,
        reviewFlags: r.warningFlags,
        fromAddress: r.fromAddress,
        toAddress: r.toAddress,
        primaryWalletAddress: r.toAddress,
        primaryWalletLabel: r.primaryWalletLabel,
        tokenContractAddress: r.tokenContractAddress,
        sourceRecordCount: r.sourceRecordCount,
        sourceSummary: r.sourceSummary,
        hasTransactionRecord: r.hasTxlistRecord,
        hasTokenTransferRecord: r.hasTokentxRecord,
        hasInternalTxRecord: r.hasTxlistinternalRecord,
      });
      continue;
    }

    if (r.normalizedTransactionType === 'SWAP' && r.assetSent && r.amountSent) {
      out.push({
        ts: r.timestampMs,
        source: 'cronos',
        kind: 'disposal',
        asset: r.assetSent,
        amount: r.amountSent,
        eurValue: null,
        cronosPriceHint: compiledPriceHint(r, r.assetSent),
        ref: r.hash,
        classification: 'TRANSFER_OUT',
        confidence: r.confidence,
        reviewFlags: r.warningFlags,
        explanation: `Swap leg out: ${r.explanation}`,
        fromAddress: r.fromAddress,
        toAddress: r.toAddress,
        primaryWalletAddress: r.fromAddress,
        primaryWalletLabel: r.primaryWalletLabel,
        tokenContractAddress: r.tokenContractAddress,
        sourceRecordCount: r.sourceRecordCount,
        sourceSummary: r.sourceSummary,
        hasTransactionRecord: r.hasTxlistRecord,
        hasTokenTransferRecord: r.hasTokentxRecord,
        hasInternalTxRecord: r.hasTxlistinternalRecord,
      });
      if (r.assetReceived && r.amountReceived) {
        out.push({
          ts: r.timestampMs,
          source: 'cronos',
          kind: 'acquisition',
          asset: r.assetReceived,
          amount: r.amountReceived,
          eurValue: null,
          cronosPriceHint: compiledPriceHint(r, r.assetReceived),
          ref: r.hash,
          classification: 'TRANSFER_IN',
          confidence: r.confidence,
          reviewFlags: r.warningFlags,
          explanation: `Swap leg in: ${r.explanation}`,
          fromAddress: r.fromAddress,
          toAddress: r.toAddress,
          primaryWalletAddress: r.toAddress,
          primaryWalletLabel: r.primaryWalletLabel,
          tokenContractAddress: r.tokenContractAddress,
          sourceRecordCount: r.sourceRecordCount,
          sourceSummary: r.sourceSummary,
          hasTransactionRecord: r.hasTxlistRecord,
          hasTokenTransferRecord: r.hasTokentxRecord,
          hasInternalTxRecord: r.hasTxlistinternalRecord,
        });
      }
      continue;
    }

    if (r.normalizedTransactionType === 'TRANSFER_IN' && r.amountReceived) {
      out.push({
        ts: r.timestampMs,
        source: 'cronos',
        kind: 'acquisition',
        asset: r.assetReceived ?? 'UNKNOWN',
        amount: r.amountReceived,
        eurValue: null,
        cronosPriceHint: compiledPriceHint(r, r.assetReceived ?? 'CRO'),
        ref: r.hash,
        classification: 'TRANSFER_IN',
        confidence: r.confidence,
        reviewFlags: r.warningFlags,
        explanation: r.explanation,
        fromAddress: r.fromAddress,
        toAddress: r.toAddress,
        primaryWalletAddress: r.toAddress,
        primaryWalletLabel: r.primaryWalletLabel,
        tokenContractAddress: r.tokenContractAddress,
        sourceRecordCount: r.sourceRecordCount,
        sourceSummary: r.sourceSummary,
        hasTransactionRecord: r.hasTxlistRecord,
        hasTokenTransferRecord: r.hasTokentxRecord,
        hasInternalTxRecord: r.hasTxlistinternalRecord,
      });
      continue;
    }

    if (r.normalizedTransactionType === 'TRANSFER_OUT' && r.amountSent) {
      out.push({
        ts: r.timestampMs,
        source: 'cronos',
        kind: 'disposal',
        asset: r.assetSent ?? 'UNKNOWN',
        amount: r.amountSent,
        eurValue: null,
        cronosPriceHint: compiledPriceHint(r, r.assetSent ?? 'CRO'),
        ref: r.hash,
        classification: 'TRANSFER_OUT',
        confidence: r.confidence,
        reviewFlags: r.warningFlags,
        explanation: r.explanation,
        fromAddress: r.fromAddress,
        toAddress: r.toAddress,
        primaryWalletAddress: r.fromAddress,
        primaryWalletLabel: r.primaryWalletLabel,
        tokenContractAddress: r.tokenContractAddress,
        sourceRecordCount: r.sourceRecordCount,
        sourceSummary: r.sourceSummary,
        hasTransactionRecord: r.hasTxlistRecord,
        hasTokenTransferRecord: r.hasTokentxRecord,
        hasInternalTxRecord: r.hasTxlistinternalRecord,
      });
      continue;
    }

    out.push({
      ts: r.timestampMs,
      source: 'cronos',
      kind: 'acquisition',
      asset: r.assetReceived ?? r.assetSent ?? 'UNKNOWN',
      amount: r.amountReceived ?? r.amountSent ?? new Decimal(0),
      eurValue: null,
      ref: r.hash,
      classification: 'UNKNOWN_REVIEW',
      confidence: r.confidence,
      reviewFlags: r.warningFlags,
      explanation: r.explanation,
      fromAddress: r.fromAddress,
      toAddress: r.toAddress,
      primaryWalletAddress: r.fromOwned ? r.fromAddress : r.toAddress,
      primaryWalletLabel: r.primaryWalletLabel,
      sourceRecordCount: r.sourceRecordCount,
      sourceSummary: r.sourceSummary,
      hasTransactionRecord: r.hasTxlistRecord,
      hasTokenTransferRecord: r.hasTokentxRecord,
      hasInternalTxRecord: r.hasTxlistinternalRecord,
    });
  }
  return out;
}
